import Link from "next/link";
import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";
import LanguageSwitcher from "./LanguageSwitcher";
import MobileNav from "./MobileNav";
import NavSearch from "./NavSearch";
import Image from "next/image";
import { useQuery } from "@apollo/client";
import { CategoryEntityResponseCollection, CategoryEntity } from "gql-types";
import { categoriesGql } from "gql/queries";
import { useWindowSize } from "helpers/scroll/animateScroll";
import { useTranslation } from "next-i18next";
import { signOut } from "next-auth/react";
import { useGlobalState } from "../../../context/GlobalStateContext";
import { SIGNIN_URL, SIGNUP_URL } from "../../../helpers/constants/routes";
import { categoryTranslateKey } from "../../../helpers/constants/translate";
import { ProfileMenu } from "components/ui_components/ProfileMenu";
import YawakitLogo from "components/customImages/YawakitLogo";
import SignUpPromotionModal from "../../ui_components/SignUpPromotionModal";

const Header = (props) => {
  const { content } = props;
  const size = useWindowSize();
  const [route, setRoute] = useState();
  const router = useRouter();
  const {
    isLoggedIn,
    user,
    isExternalAuth,
    clearAuthState,
    setLoading,
    addToaster,
    removeToaster,
  } = useGlobalState();
  const { t: translate, ready } = useTranslation("categories");
  const { t: translateLayout } = useTranslation("layout");

  const [mobileSearch, setMobileSearch] = useState(false);
  const [shadow, setShadow] = useState(false);
  const [categories, setCategories] = useState<CategoryEntity[]>([]);
  const [displaySearchBar, setDisplaySearchBar] = useState(false);
  const divRef = useRef(null);

  const { loading, data } = useQuery<{ categories?: CategoryEntityResponseCollection }>(
    categoriesGql,
  );

  useEffect(() => {
    window.addEventListener("scroll", () => setShadow(window.scrollY >= 90));
  }, [router]);

  useEffect(() => {
    if (!loading) {
      setCategories(data?.categories?.data || []);
    }
  }, [loading]);

  const { direction } = useGlobalState();

  if (!ready) {
    return null;
  }

  const logout = async () => {
    setLoading(true);
    try {
      if (isExternalAuth) {
        await signOut({ redirect: false });
      }
      clearAuthState();
      await router.push("/");
    } catch (error) {
      addToaster({
        message: translateLayout("logout_error"),
        type: "error",
        callback: (index: number) => removeToaster(index),
      });
    } finally {
      setLoading(false);
    }
  };

  const navigateTo = (path: string) => {
    const currentUrl = router.asPath;
    const { next_url } = router.query as { next_url?: string };
    router
      .push({
        pathname: path,
        query: { next_url: currentUrl.startsWith("/auth/") ? next_url : currentUrl },
      })
      .then();
  };

  const navigateToCategory = (_category: string) => {
    router.push(`/categories/${_category}`).then();
  };

  const handleDisplaySearchBar = () => setDisplaySearchBar(!displaySearchBar);
  const isCategoryActive = router.pathname.startsWith("/categories");

  const dropdownClass = `dropdown p-2 rounded-sm text-lg cursor-pointer ${
    isCategoryActive ? "text-[#233377] underline" : ""
  }`;
  const selectedNavbarItem = (path: string) => {
    const isActive = path === `${router.pathname}${window.location.hash}`;

    return `p-2 rounded-sm text-lg ${isActive ? "text-[#233377] underline" : ""}`;
  };

  return (
    <header
      ref={divRef}
      className={`sticky top-0 bg-white z-[999] ${shadow ? "shadow" : "shadow-lg lg:shadow-none"}`}
    >
      {!displaySearchBar ? (
        <nav className="z-[1000] relative bg-white">
          <div
            className={`flex justify-between px-1 py-8 md:px-8 mx-auto navbar-y-10 font-normal text-neutral-800 relative z-50 ${direction === "ltr" ? "rtl:flex-row-reverse" : "ltr:flex-row"}`}
          >
            <MobileNav route={route || "/"} content={content} />
            <div
              className={`hover:cursor-pointer my-auto mx-2 min-w-[80px] ${size.width <= 350 ? "mr-10" : ""}`}
            >
              <Link href="/">
                <div className="md:hidden">
                  <Image
                    src={mobileSearch ? "/favicon.ico" : "/images/yawakit_logo.png"}
                    alt="yawakit logo"
                    width={122}
                    height={32}
                    style={{ maxWidth: "100%", height: "auto" }}
                  />
                </div>
              </Link>
              <Link href="/">
                <div className="hidden md:flex">
                  <YawakitLogo
                    imageName={"yawakit_logo.png"}
                    alt={translate("image_not_found")}
                    className={""}
                  />
                </div>
              </Link>
            </div>
            <div className="lg:hidden w-2"></div>
            <div className="hidden lg:flex justify-between min-w-max gap-4 xl:gap-20 items-center ">
              <Link href="/about">
                <button className={selectedNavbarItem("/about")}>{content("about")}</button>
              </Link>
              <div className={dropdownClass}>
                <span>{content("categories")}</span>
                <div className="dropdown-content">
                  <div className="grid grid-cols-3 gap-y-2 py-2">
                    {categories.map((item: any, index: number) => {
                      const isSelected = router.query.category === item.attributes.slug;
                      return (
                        <div
                          className="text-center hover:bg-[#D9D9D9] hover:rounded-lg py-2 cursor-pointer"
                          key={`category_${index}`}
                          onClick={() => navigateToCategory(item.attributes.slug)}
                        >
                          <p
                            className={isSelected ? "text-[#233377]  text-base font-extrabold" : ""}
                          >
                            {translate(categoryTranslateKey(item.attributes.slug))}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
              <Link href="/offers">
                <button className={selectedNavbarItem("/offers")}>{content("offers")}</button>
              </Link>
              <Link href="/faq">
                <button className={selectedNavbarItem("/faq")}>{content("faq")}</button>
              </Link>
              <Link href="/blog">
                <button className={selectedNavbarItem("/blog")}>{content("blog")}</button>
              </Link>
              {/* a tag is needed for full refresh  */}
              {/* eslint-disable-next-line @next/next/no-html-link-for-pages */}
              <a href="/#contact" className="p-0 m-0">
                <button className={selectedNavbarItem("/#contact")}>{content("contact")}</button>
              </a>
            </div>
            <div className="hidden lg:flex justify-between items-center gap-2">
              <NavSearch
                setMobileSearch={setMobileSearch}
                mobileSearch={mobileSearch}
                content={content}
                categories={categories}
                isSearchInputDisplayed={true}
              />
              <LanguageSwitcher />
              <div className="h-[35px] border-r-2"></div>
              {isLoggedIn ? (
                <ProfileMenu
                  name={user.name}
                  picture={user.picture}
                  translate={content}
                  logout={logout}
                  direction={direction}
                />
              ) : (
                <>
                  <button
                    id="sign_up"
                    onClick={() => navigateTo(SIGNUP_URL)}
                    className="border border-[#233377] w-[120px] h-[45px] rounded-md text-[#233377] font-semibold hover:bg-slate-100"
                  >
                    {content("signup")}
                  </button>
                  <div className="h-[35px] border-r-2"></div>
                  <button
                    id="login"
                    className="text-white font-semibold bg-[#233377] w-[120px] h-[45px] rounded-md m-auto"
                    onClick={() => navigateTo(SIGNIN_URL)}
                  >
                    {content("signin")}
                  </button>
                </>
              )}
            </div>

            <div
              className={`lg:hidden cursor-pointer flex items-center gap-2 ${direction === "ltr" ? "rtl:flex-row-reverse" : "ltr:flex-row"}`}
            >
              <div className="flex items-center" onClick={handleDisplaySearchBar}>
                <Image
                  height={45}
                  width={45}
                  src={"/images/gray-search-icon.svg"}
                  alt={"search-icon"}
                />
              </div>

              {isLoggedIn ? (
                <ProfileMenu
                  name={user.name}
                  picture={user.picture}
                  translate={content}
                  logout={logout}
                  direction={direction}
                />
              ) : (
                <button
                  id="login_sign_up"
                  className="text-white font-semibold bg-[#233377] w-[120px] h-[45px] rounded-md m-auto"
                  onClick={() => navigateTo(SIGNIN_URL)}
                >
                  {content("signin")}
                </button>
              )}
            </div>
          </div>
        </nav>
      ) : (
        <div className="flex justify-center lg:justify-start items-center gap-2 h-[100%]">
          <div className="flex items-center gap-2">
            <NavSearch
              suppressHydrationWarning={true}
              setMobileSearch={setMobileSearch}
              mobileSearch={mobileSearch}
              content={content}
              categories={categories}
              isSearchInputDisplayed={displaySearchBar}
              handleDisplaySearchBar={handleDisplaySearchBar}
            />
          </div>
        </div>
      )}
      <SignUpPromotionModal />
    </header>
  );
};

export default Header;
